import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Link
} from '@mui/material';
import {
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { CiImport } from 'react-icons/ci';
import Icon from '../../../../shared/components/Icon';

// Cryptique Design System
const styles = {
  primarycolor: "#0f172a",
  accentColor: "#caa968",
  backgroundColor: "#ffffff",
  cardBg: "white",
  textPrimary: "#0f172a",
  textSecondary: "#666666",
  successColor: "#0f766e",
  warningColor: "#b45309",
  errorColor: "#b91c1c",
  headingFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 600 },
  bodyFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 400 },
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
};

const SubscriptionSummary = ({
  subscription,
  onManageSubscription,
  manageLoading = false
}) => {
  if (!subscription) return null;

  const planNames = {
    free: 'Free',
    starter: 'Starter',
    pro: 'Pro',
    enterprise: 'Enterprise'
  };

  const statusConfig = {
    active: { label: 'ACTIVE', color: styles.successColor, icon: CheckCircle },
    past_due: { label: 'PAST DUE', color: styles.warningColor, icon: AlertTriangle },
    cancelled: { label: 'CANCELLED', color: styles.errorColor, icon: XCircle },
    inactive: { label: 'INACTIVE', color: styles.textSecondary, icon: XCircle }
  };

  const planLabel = planNames[subscription.plan?.toLowerCase?.()] || (subscription.plan ? String(subscription.plan) : 'Plan');
  const statusKey = subscription.status?.toLowerCase?.();
  const statusMeta = statusConfig[statusKey] || {
    label: subscription.status?.toUpperCase?.() || 'UNKNOWN',
    color: styles.textSecondary,
    icon: CheckCircle
  };
  const StatusIcon = statusMeta.icon;

  const formatDate = (value) => {
    if (!value) return 'N/A';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'Custom';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const nextBillingDate = subscription.nextBillingDate || subscription.currentPeriodEnd;
  const cost = subscription.monthlyCost ?? subscription.amount;
  const billingCycleLabel = subscription.billingCycle === 'annual'
    ? 'Annual'
    : subscription.billingCycle === 'monthly'
      ? 'Monthly'
      : null;

  return (
    <Box
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        backgroundColor: styles.backgroundColor,
        mb: 3,
        p: 2.5
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ ...styles.headingFont, color: styles.textPrimary, fontSize: '1.1rem' }}>
              {planLabel} Plan
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<StatusIcon size={14} />}
                label={statusMeta.label}
                size="small"
                sx={{
                  border: `1px solid ${statusMeta.color}`,
                  backgroundColor: 'transparent',
                  color: statusMeta.color,
                  fontWeight: 500,
                  borderRadius: '4px',
                  fontSize: '0.75rem'
                }}
              />
              {subscription.hasCQIntelligence && (
                <Chip
                  label="CQ Intelligence"
                  size="small"
                  sx={{
                    border: `1px solid ${styles.accentColor}`,
                    backgroundColor: 'transparent',
                    color: styles.accentColor,
                    fontWeight: 500,
                    borderRadius: '4px',
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" sx={{ ...styles.bodyFont, color: styles.textSecondary, fontSize: '0.8rem', display: 'inline' }}>
                Next Billing Date: 
              </Typography>
              <Typography variant="body2" sx={{ ...styles.bodyFont, fontWeight: 500, fontSize: '0.875rem', display: 'inline', ml: 0.5 }}>
                {formatDate(nextBillingDate)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ ...styles.bodyFont, color: styles.textSecondary, fontSize: '0.8rem', display: 'inline' }}>
                Billing Cycle: 
              </Typography>
              <Typography variant="body2" sx={{ ...styles.bodyFont, fontWeight: 500, textTransform: 'capitalize', fontSize: '0.875rem', display: 'inline', ml: 0.5 }}>
                {billingCycleLabel || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={onManageSubscription}
          disabled={manageLoading}
          sx={{
            background: '#4b57aa',
            color: '#fff',
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            px: 2,
            py: 0.75,
            boxShadow: 'none',
            '&:hover': {
              background: '#4b57aa',
              opacity: 0.9,
              boxShadow: 'none'
            },
            '&:disabled': {
              background: '#DDE2EE',
              color: '#475569'
            }
          }}
        >
          {manageLoading ? 'Opening Portal...' : 'Manage Subscription'}
        </Button>
      </Box>
    </Box>
  );
};

// Invoice row component
const InvoiceRow = ({ invoice }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Shorten invoice ID (show last 8 characters)
  const shortenedInvoiceId = invoice.id ? invoice.id.slice(-8).toUpperCase() : 'N/A';
  
  // Capitalize plan type
  const planTypeDisplay = invoice.planType 
    ? invoice.planType.charAt(0).toUpperCase() + invoice.planType.slice(1)
    : invoice.planName || 'N/A';
  
  // Capitalize billing cycle
  const billingCycleDisplay = invoice.billingCycle
    ? invoice.billingCycle.charAt(0).toUpperCase() + invoice.billingCycle.slice(1)
    : 'N/A';

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: '#F9FAFB'
        }
      }}
    >
      <TableCell align="center">
        <Typography variant="body2" sx={{ ...styles.bodyFont, fontWeight: 500, fontSize: '0.875rem', fontFamily: 'monospace' }}>
          {shortenedInvoiceId}
        </Typography>
      </TableCell>
      
      <TableCell align="center">
        <Typography variant="body2" sx={{ ...styles.bodyFont, fontSize: '0.875rem' }}>
          {planTypeDisplay}
        </Typography>
      </TableCell>
      
      <TableCell align="center">
        <Typography variant="body2" sx={{ ...styles.bodyFont, fontSize: '0.875rem' }}>
          {billingCycleDisplay}
        </Typography>
      </TableCell>
      
      <TableCell align="center">
        <Typography variant="body2" sx={{ ...styles.bodyFont, fontWeight: 500, fontSize: '0.875rem' }}>
          {formatCurrency(invoice.amount)}
        </Typography>
      </TableCell>
      
      <TableCell align="center">
        <Typography variant="body2" sx={{ ...styles.bodyFont, fontSize: '0.875rem' }}>
          {formatDate(invoice.period?.start)}
        </Typography>
      </TableCell>
      
      <TableCell align="center">
        <Typography variant="body2" sx={{ ...styles.bodyFont, fontSize: '0.875rem' }}>
          {formatDate(invoice.period?.end)}
        </Typography>
      </TableCell>
      
      <TableCell align="right">
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          {invoice.downloadUrl && (
            <Tooltip title="Download Invoice">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(invoice.downloadUrl, '_blank');
                }}
                sx={{ color: styles.primaryColor }}
              >
                <CiImport size={18} />
              </IconButton>
            </Tooltip>
          )}
          
          {invoice.viewUrl && (
            <Tooltip title="View Invoice">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(invoice.viewUrl, '_blank');
                }}
                sx={{ color: styles.primaryColor }}
              >
                <Icon name="share" size={18} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

// Main billing history component
const BillingHistory = ({
  invoices = [],
  loading = false,
  subscription,
  onManageSubscription,
  manageLoading
}) => {

  if (loading) {
    return (
      <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', p: 3 }}>
        <Typography sx={{ ...styles.bodyFont, color: styles.textSecondary }}>Loading billing history...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SubscriptionSummary
        subscription={subscription}
        onManageSubscription={onManageSubscription}
        manageLoading={manageLoading}
      />

      {/* Billing History Table */}
      <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: styles.backgroundColor, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ ...styles.headingFont, color: styles.textPrimary, fontSize: '1rem' }}>
            Billing History
          </Typography>
          <Link
            href="https://www.cryptique.io/pricing"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...styles.bodyFont,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: styles.primarycolor,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            What&apos;s included? →
          </Link>
        </Box>

        {invoices.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ ...styles.headingFont, color: styles.textSecondary, fontSize: '1rem' }}>
              No billing history yet
            </Typography>
            <Typography variant="body2" sx={{ ...styles.bodyFont, color: styles.textSecondary, mt: 1, fontSize: '0.875rem' }}>
              Your invoices and payment history will appear here once you start using paid features.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                  <TableCell align="center" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Invoice ID</TableCell>
                  <TableCell align="center" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Plan Type</TableCell>
                  <TableCell align="center" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Billing Cycle</TableCell>
                  <TableCell align="center" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Amount</TableCell>
                  <TableCell align="center" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Period Start</TableCell>
                  <TableCell align="center" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Period End</TableCell>
                  <TableCell align="right" sx={{ ...styles.headingFont, textTransform: 'uppercase', color: styles.primaryColor, fontSize: '0.75rem', py: 1.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default BillingHistory;
